import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { findRowFromRows, RemoteTable } from '@neo/blocks/data-table/data-table'
import { Button, ButtonWithLoader } from '@neo/index'
import { useSheet } from '@shared/hooks/use-sheet'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { SideSheet } from 'v2/shared/components/side-sheet'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { useDeleteSecretVariableMutation } from '../queries'
import { SecretVariableFormComponent } from './secret-variable-form'
import { columns, filterFields, type SecretVariableForm } from './secrets.config'

export const SecretSheet = () => {
  const { sheetData, closeSheet } = useSheet<Partial<SecretVariableForm>>()
  const [isPending, setIsPending] = useState(false)
  const defaultData = useMemo(
    () => ({
      id: sheetData?.id,
      name: sheetData?.name || '',
      value: '',
      description: sheetData?.description || '',
    }),
    [sheetData],
  )
  if (!sheetData) return null
  const isEdit = !!sheetData.id
  return (
    <SideSheet
      title={isEdit ? 'Edit Secret' : 'Create Secret'}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" onClick={closeSheet} variant="outline">
            Cancel
          </Button>
          <ButtonWithLoader type="submit" form="secret-variable-form" loading={isPending}>
            {isEdit ? 'Update' : 'Create'} Secret
          </ButtonWithLoader>
        </div>
      }
    >
      <SecretVariableFormComponent
        defaultValues={defaultData}
        onSuccess={closeSheet}
        setIsPending={setIsPending}
      />
    </SideSheet>
  )
}

export const SecretsPage = () => {
  const sheet = useSheet<Partial<SecretVariableForm> | boolean>()
  const { mutate: deleteSecretVariable } = useDeleteSecretVariableMutation()
  const [popupState, setPopupState] = useState({
    open: false,
    id: '',
    name: '',
  })
  return (
    <div className="flex h-full min-h-0 flex-col">
      <RemoteTable
        ActionButton={
          <Button
            data-testid="button-create-secret"
            type="button"
            onClick={() => sheet.openSheet(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Secret
          </Button>
        }
        queryKey={flowQueryKeys().secretVariables}
        columns={columns}
        clientTable
        getContextMenu={(...args) => [
          {
            label: 'Actions',
            type: 'label',
          },
          {
            label: 'Delete',
            type: 'item',
            shortcut: {
              key: 'backspace',
            },
            onSelect: () => {
              const selectedRow = findRowFromRows(args[1], {
                id: args[2]?.current?.dataset.hover || '',
              })
              if (!selectedRow) return
              setPopupState({
                ...popupState,
                open: true,
                id: selectedRow.id,
                name: selectedRow.name,
              })
            },
          },
        ]}
        onRowClick={(row) => sheet.openSheet(row)}
        filterFields={filterFields}
        fetchService={flowService.listSecretVariables}
        extractData={(resp) => ({ data: resp, totalCount: resp.length })}
      />
      <ConfirmPopup
        open={popupState.open}
        setOpen={(open) => setPopupState({ ...popupState, open })}
        title="Delete Secret Variable"
        description={`Are you sure you want to delete the secret variable ${popupState.name}?`}
        okButtonProps={{
          variant: 'destructive',
          onClick: () => {
            deleteSecretVariable(popupState.id)
          },
        }}
      />
    </div>
  )
}
